export async function fetchJson<T = any>(input: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(input, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) {
    const message = data?.message ?? res.statusText;
    throw new Error(message || 'Request failed');
  }
  return data;
}

// Backward compatibility - export api object for existing code
export const api = {
  get: (url: string) => fetchJson(url),
  post: (url: string, data: any) => fetchJson(url, { method: 'POST', body: JSON.stringify(data) }),
  put: (url: string, data: any) => fetchJson(url, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (url: string) => fetchJson(url, { method: 'DELETE' }),
};

// Sessions API types
export interface Session {
  id: number;
  title: string;
  description: string | null;
  sessionType: string;
  ageGroup: string;
  location: string;
  startTime: string;
  endTime: string;
  maxPlayers: number | null;
  coachId: number;
  academyId?: number;
  createdAt: string;
  updatedAt: string;
}

export interface SessionsResponse {
  ok: boolean;
  sessions: Session[];
}

// GET /api/parent/sessions's shape — each session carries the parent's own
// kid(s) whose batch matches, so the client never guesses which kid an
// RSVP applies to.
export interface ParentSessionKid {
  id: number;
  firstName: string;
  lastName: string;
}

export interface ParentSession extends Omit<Session, "academyId"> {
  batchId: number | null;
  kids: ParentSessionKid[];
}

export interface ParentSessionsResponse {
  success: boolean;
  sessions: ParentSession[];
}

export interface CreateSessionData {
  title: string;
  description?: string;
  sessionType: string;
  ageGroup: string;
  location: string;
  startTime: string; // ISO 8601 format
  endTime: string; // ISO 8601 format
  maxPlayers?: number;
}

export interface CreateSessionResponse {
  ok: boolean;
  session: Session;
}

// Sessions API helpers
export async function fetchSessions(upcoming = true): Promise<SessionsResponse> {
  const url = upcoming ? '/api/sessions?upcoming=true' : '/api/sessions';
  return fetchJson<SessionsResponse>(url);
}

// Server-side scoped, batch-membership-aware equivalent of fetchSessions
// for the parent schedule view — see server/routes/parent-availability.ts
// for why this exists instead of narrowing GET /api/sessions in place.
export async function fetchParentSessions(upcoming = true): Promise<ParentSessionsResponse> {
  const url = upcoming ? "/api/parent/sessions?upcoming=true" : "/api/parent/sessions";
  return fetchJson<ParentSessionsResponse>(url);
}

export async function createSession(data: CreateSessionData): Promise<CreateSessionResponse> {
  return fetchJson<CreateSessionResponse>('/api/sessions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Session Availability API types (Wave 1 Mini-MVP - Read-Only)
// status vocabulary standardized on yes/no/maybe (2026-08) — see
// DOCS/sprint-log.md for why going/not_going/unsure was dropped.
export interface SessionAvailabilityRecord {
  id: number;
  sessionId: number;
  parentId: number;
  kidId: number;
  status: 'yes' | 'no' | 'maybe';
  respondedAt: string | null;
  createdAt: string;
  updatedAt: string;
  kid: {
    id: number;
    firstName: string;
    lastName: string;
    ageGroup: string;
  };
  parent: {
    id: number;
    fullName: string;
    email: string;
  };
}

export interface SessionAvailabilitySummary {
  yes: number;
  no: number;
  maybe: number;
  total: number;
}

export interface SessionAvailabilityResponse {
  ok: boolean;
  availability: SessionAvailabilityRecord[];
  summary: SessionAvailabilitySummary;
}

// Session Availability API helpers
export async function fetchSessionAvailability(sessionId: number): Promise<SessionAvailabilityResponse> {
  return fetchJson<SessionAvailabilityResponse>(`/api/sessions/${sessionId}/availability`);
}